import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { HelpDrawer } from '@/help/HelpDrawer'
import { useHelpContextSlug } from '@/help/useHelpContext'
import { cn } from '@/lib/cn'
import { useHotkeys } from '@/lib/hotkeys'
import { readStorage, writeStorage } from '@/lib/storage'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { CommandPalette } from './CommandPalette'
import { NAV_ITEMS } from './nav'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'

/**
 * Sidebar + top bar + scrolling content. Pages render inside `children`
 * with standard padding; routes with `staticData.fullBleed` get the whole
 * area (for split-view editors like the template studio).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const wide = useMediaQuery('(min-width: 1180px)')
  const [userCollapsed, setUserCollapsed] = useState(
    () => readStorage('sidebarCollapsed') === 'true',
  )
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const helpSlug = useHelpContextSlug()
  const collapsed = !wide || userCollapsed
  const fullBleed = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData?.fullBleed),
  })

  const toggleSidebar = () => {
    setUserCollapsed((c) => {
      writeStorage('sidebarCollapsed', String(!c))
      return !c
    })
  }

  useHotkeys([
    { keys: 'mod+k', handler: () => setPaletteOpen((o) => !o), allowInInputs: true },
    { keys: '[', handler: () => wide && toggleSidebar() },
    { keys: '?', handler: () => setHelpOpen((o) => !o) },
    ...NAV_ITEMS.filter((i) => i.shortcut).map((i) => ({
      keys: i.shortcut!,
      handler: () => void navigate({ to: i.to }),
    })),
  ])

  return (
    <div className="flex h-dvh overflow-hidden bg-bg">
      <a
        href="#main"
        className="sr-only z-50 rounded-md bg-surface px-3 py-2 focus:not-sr-only focus:absolute focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      <Sidebar collapsed={collapsed} canToggle={wide} onToggle={toggleSidebar} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenPalette={() => setPaletteOpen(true)} />
        <main
          id="main"
          className={cn('min-h-0 flex-1', fullBleed ? 'overflow-hidden' : 'overflow-y-auto')}
          tabIndex={-1}
        >
          {fullBleed ? (
            children
          ) : (
            <div className="mx-auto w-full max-w-[1440px] px-5 pt-6 pb-16 lg:px-8">{children}</div>
          )}
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onOpenHelp={() => setHelpOpen(true)}
      />
      <HelpDrawer open={helpOpen} onOpenChange={setHelpOpen} slug={helpSlug} />
    </div>
  )
}
